import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CITATION_PLAIN,
  CITATION_REPO,
  CONTRIBUTIONS,
  KEY_FINDINGS,
  ManifestDoc,
  PUBLIC_AGENCIES,
  REPRO_STATUS,
  RESEARCH_REPO,
  STUDY_SUMMARY,
} from '../../../constants/transportationResearchRepo';
import './MunicipalTransportationEmissionsPage.css';

const BASE = `${process.env.PUBLIC_URL || ''}/research/municipal-transportation-emissions`;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function groupFiles(files: ManifestDoc['files'], types: string[]) {
  return files.filter((f) => types.includes(f.type));
}

export const MunicipalTransportationEmissionsPage: React.FC = () => {
  const [manifest, setManifest] = useState<ManifestDoc | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    document.title = `${RESEARCH_REPO.title} | ClimatePlans Research Repository`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        'content',
        `${RESEARCH_REPO.title}. Data, code, and reproducibility materials for harmonized California municipal transportation emissions analysis.`
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = RESEARCH_REPO.canonicalUrl;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('property', 'og:type', 'article');
    setMeta('property', 'og:url', RESEARCH_REPO.canonicalUrl);
    setMeta('property', 'og:title', `${RESEARCH_REPO.title} | ClimatePlans`);
    setMeta(
      'property',
      'og:description',
      `${RESEARCH_REPO.title}. Data, code, and reproducibility materials for harmonized California municipal transportation emissions analysis.`
    );
    setMeta('property', 'og:image', `${window.location.origin}/images/climateplans-og-image.png`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', `${RESEARCH_REPO.title} | ClimatePlans`);
    setMeta(
      'name',
      'twitter:description',
      `${RESEARCH_REPO.title}. Data, code, and reproducibility materials for harmonized California municipal transportation emissions analysis.`
    );

    const scriptId = 'transportation-research-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ScholarlyArticle',
          name: RESEARCH_REPO.title,
          headline: RESEARCH_REPO.title,
          description: STUDY_SUMMARY,
          author: RESEARCH_REPO.authors.map((name) => ({ '@type': 'Person', name })),
          datePublished: String(RESEARCH_REPO.year),
          isPartOf: { '@type': 'PublicationIssue', name: RESEARCH_REPO.specialIssue },
          publisher: { '@type': 'Organization', name: 'ClimatePlans' },
          url: RESEARCH_REPO.canonicalUrl,
          keywords:
            'transportation, greenhouse gas, California municipalities, EMFAC, LMDI',
          spatialCoverage: { '@type': 'Place', name: 'California' },
          temporalCoverage: '2005/2025',
          funding: {
            '@type': 'MonetaryGrant',
            identifier: 'R02CP7580',
            funder: { '@type': 'Organization', name: 'University of California' },
          },
        },
        {
          '@type': 'Dataset',
          name: 'ClimatePlans Municipal Transportation Emissions Analysis',
          version: RESEARCH_REPO.release,
          url: RESEARCH_REPO.canonicalUrl,
          license: 'https://creativecommons.org/licenses/by/4.0/',
        },
        {
          '@type': 'SoftwareSourceCode',
          name: 'ClimatePlans transportation decomposition scripts',
          programmingLanguage: 'Python',
          url: RESEARCH_REPO.canonicalUrl,
          license: 'https://opensource.org/licenses/MIT',
        },
      ],
    });

    fetch(`${BASE}/manifest.json`)
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => setManifest(null));
  }, []);

  const pendingByType = useMemo(() => {
    if (!manifest?.pending) return { publication: [], data: [], code: [], figure: [], documentation: [] };
    const groups: Record<string, { title: string }[]> = {
      publication: [],
      data: [],
      code: [],
      figure: [],
      documentation: [],
    };
    for (const p of manifest.pending) {
      if (groups[p.type]) groups[p.type].push(p);
    }
    return groups;
  }, [manifest]);

  const pubFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['publication']) : []),
    [manifest]
  );
  const dataFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['data']) : []),
    [manifest]
  );
  const codeFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['code']) : []),
    [manifest]
  );
  const figureFiles = useMemo(
    () => (manifest ? groupFiles(manifest.files, ['figure']) : []),
    [manifest]
  );

  const copyText = useCallback(async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  }, []);

  const renderDownloadCard = (f: ManifestDoc['files'][0]) => (
    <li key={f.download_path} className="trepo-dl-item">
      <a href={f.download_path} className="trepo-dl-link" download>
        <span className="trepo-dl-title">{f.title}</span>
        <span className="trepo-dl-meta">
          {f.filename.split('.').pop()?.toUpperCase()} · {formatBytes(f.size_bytes)} ·{' '}
          {f.version}
        </span>
      </a>
    </li>
  );

  const renderPending = (title: string) => (
    <li key={title} className="trepo-dl-item trepo-dl-item--pending">
      <span className="trepo-dl-title">{title}</span>
      <span className="trepo-dl-meta">Pending publication release</span>
    </li>
  );

  return (
    <article className="trepo-page" aria-labelledby="trepo-title">
      <header className="trepo-hero">
        <p className="trepo-kicker">Research Repository</p>
        <h1 id="trepo-title" className="trepo-h1">
          {RESEARCH_REPO.title}
        </h1>
        {RESEARCH_REPO.subtitle ? (
          <p className="trepo-subtitle">{RESEARCH_REPO.subtitle}</p>
        ) : null}
        <p className="trepo-authors">{RESEARCH_REPO.authors.join(' · ')}</p>
        <div className="trepo-badges">
          <span className="trepo-badge">{RESEARCH_REPO.statusBadge}</span>
          <span className="trepo-badge trepo-badge--muted">DOI pending</span>
        </div>
        <p className="trepo-meta-line">
          <span>Special issue: {RESEARCH_REPO.specialIssue}</span>
          <span>Release {RESEARCH_REPO.release}</span>
          <span>Last updated {RESEARCH_REPO.lastUpdated}</span>
        </p>
        <div className="trepo-hero-actions">
          <button
            type="button"
            className="trepo-btn trepo-btn--primary"
            onClick={() => copyText(CITATION_PLAIN, 'cite')}
          >
            {copied === 'cite' ? 'Copied' : 'Copy citation'}
          </button>
          <a className="trepo-btn trepo-btn--secondary" href={`${BASE}/releases/${RESEARCH_REPO.release}/`}>
            Browse release files
          </a>
          <a className="trepo-btn trepo-btn--secondary" href={`${BASE}/citation.bib`} download>
            Download BibTeX
          </a>
        </div>
      </header>

      <section className="trepo-section" aria-labelledby="trepo-summary">
        <h2 id="trepo-summary">Study summary</h2>
        <p className="trepo-lead">{STUDY_SUMMARY}</p>
        <div className="trepo-cards">
          {CONTRIBUTIONS.map((c) => (
            <div key={c.title} className="trepo-card">
              <h3>{c.title}</h3>
              <p>{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-findings">
        <h2 id="trepo-findings">Key findings</h2>
        <dl className="trepo-findings">
          {KEY_FINDINGS.map((f) => (
            <div key={f.label} className="trepo-finding">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
        <p className="trepo-note">
          Diagnostic attribution subdivides the composite emission-factor term but is not
          independently additive to the formal LMDI. EMFAC forward trajectories are
          model-release scenarios, not forecasts.
        </p>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-downloads">
        <h2 id="trepo-downloads">Downloads</h2>
        <div className="trepo-dl-grid">
          <div className="trepo-dl-group">
            <h3>Publication</h3>
            <ul>
              {pendingByType.publication.map((p) => renderPending(p.title))}
              {figureFiles.map(renderDownloadCard)}
              {pendingByType.figure.map((p) => renderPending(p.title))}
              <li className="trepo-dl-item">
                <a href={`${BASE}/citation.bib`} className="trepo-dl-link" download>
                  <span className="trepo-dl-title">Citation (BibTeX)</span>
                </a>
              </li>
              <li className="trepo-dl-item">
                <a href={`${BASE}/citation.ris`} className="trepo-dl-link" download>
                  <span className="trepo-dl-title">Citation (RIS)</span>
                </a>
              </li>
              <li className="trepo-dl-item">
                <a href={`${BASE}/CITATION.cff`} className="trepo-dl-link" download>
                  <span className="trepo-dl-title">CITATION.cff</span>
                </a>
              </li>
            </ul>
          </div>
          <div className="trepo-dl-group">
            <h3>Analytical data</h3>
            <ul>
              {dataFiles.map(renderDownloadCard)}
              {pendingByType.data.map((p) => renderPending(p.title))}
            </ul>
          </div>
          <div className="trepo-dl-group">
            <h3>Code and reproducibility</h3>
            <ul>
              {codeFiles.map(renderDownloadCard)}
              <li className="trepo-dl-item">
                <a
                  href={`${BASE}/releases/${RESEARCH_REPO.release}/documentation/REPRODUCIBILITY.md`}
                  className="trepo-dl-link"
                  download
                >
                  <span className="trepo-dl-title">Reproducibility guide (Markdown)</span>
                </a>
              </li>
              <li className="trepo-dl-item">
                <a href={`${BASE}/checksums.sha256`} className="trepo-dl-link" download>
                  <span className="trepo-dl-title">SHA-256 checksums</span>
                </a>
              </li>
              {pendingByType.code.map((p) => renderPending(p.title))}
              {pendingByType.documentation.map((p) => renderPending(p.title))}
            </ul>
          </div>
        </div>
        {pubFiles.length === 0 && null}
      </section>

      <section className="trepo-section trepo-restrict" aria-labelledby="trepo-data">
        <h2 id="trepo-data">Data availability and restrictions</h2>
        <p>
          Public input data are available from the California Department of Transportation, the
          California Air Resources Board EMFAC model and Fleet Database, the U.S. Census Bureau,
          and the California Department of Finance. Derived analytical tables, figure data, and
          analysis scripts supporting the findings are provided through this repository. Replica
          origin–destination data are proprietary and cannot be redistributed. Recreating the
          direct 2023–2025 municipal activity inputs requires licensed access to Replica.
        </p>
        <p className="trepo-warning" role="note">
          <strong>Raw Replica data are not included.</strong>
        </p>
        <ul className="trepo-agencies">
          {PUBLIC_AGENCIES.map((a) => (
            <li key={a.url}>
              <a href={a.url} rel="noopener noreferrer">
                {a.name}
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-repro">
        <h2 id="trepo-repro">Reproducibility</h2>
        <table className="trepo-table">
          <caption className="trepo-sr-only">Reproducibility status by component</caption>
          <thead>
            <tr>
              <th scope="col">Component</th>
              <th scope="col">Reproducible from public files</th>
              <th scope="col">Restriction</th>
            </tr>
          </thead>
          <tbody>
            {REPRO_STATUS.map(([comp, yes, note]) => (
              <tr key={comp}>
                <td>{comp}</td>
                <td>{yes}</td>
                <td>{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-versions">
        <h2 id="trepo-versions">Repository versions</h2>
        <div className="trepo-version-card">
          <h3>
            Version {RESEARCH_REPO.release} — {RESEARCH_REPO.releaseLabel}
          </h3>
          <ul>
            <li>
              <strong>Date:</strong> {RESEARCH_REPO.lastUpdated}
            </li>
            <li>
              <strong>Methods version:</strong> Release 2026.1
            </li>
            <li>
              <strong>Manuscript version:</strong> Stage 7 submission-ready (pre-DOI)
            </li>
            <li>
              <strong>Reserved:</strong> v1.0.0 for frozen publication release with archive DOI
            </li>
            <li>
              <strong>DOI:</strong> Pending
            </li>
            <li>
              <strong>GitHub release:</strong> Pending
            </li>
            <li>
              <strong>Zenodo record:</strong> Pending
            </li>
          </ul>
        </div>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-cite">
        <h2 id="trepo-cite">Citation</h2>
        <h3>Article</h3>
        <pre className="trepo-pre">{CITATION_PLAIN}</pre>
        <button
          type="button"
          className="trepo-btn trepo-btn--small"
          onClick={() => copyText(CITATION_PLAIN, 'article')}
        >
          {copied === 'article' ? 'Copied' : 'Copy'}
        </button>
        <h3>Repository</h3>
        <pre className="trepo-pre">{CITATION_REPO}</pre>
        <button
          type="button"
          className="trepo-btn trepo-btn--small"
          onClick={() => copyText(CITATION_REPO, 'repo')}
        >
          {copied === 'repo' ? 'Copied' : 'Copy'}
        </button>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-funding">
        <h2 id="trepo-funding">Funding</h2>
        <p>{RESEARCH_REPO.funding}</p>
      </section>

      <section className="trepo-section" aria-labelledby="trepo-related">
        <h2 id="trepo-related">Related resources</h2>
        <ul className="trepo-links">
          <li>
            <Link to="/">ClimatePlans homepage</Link>
          </li>
          <li>
            <Link to="/transportation">Transportation data and methods</Link>
          </li>
          <li>GitHub repository — pending</li>
          <li>Zenodo archive — pending</li>
          <li>Published paper — pending</li>
        </ul>
      </section>
    </article>
  );
};

export default MunicipalTransportationEmissionsPage;
